import { useUsersApi } from "@/Hooks/useUsersApi";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store";
import { storeCoupon } from "@/Slices/couponSlice";
import { isUnauthorizedError } from "@/Helpers/isUnauthorizedError";
import { isNetworkError } from "@/Helpers/isNetworkError";

// Extended Quote type with original price
export interface QuoteWithOriginalPrice extends Quote {
  priceBeforeCoupon?: number | undefined;
}

/**
 * Applies a coupon amount to a quote. Discount is clamped so price and tax
 * never go below zero (e.g. $56.96 quote with $100 coupon → $0.00).
 */
export function applyCouponToQuote(
  q: Quote,
  couponAmount: number | undefined,
): QuoteWithOriginalPrice {
  const qCopy = { ...q } as QuoteWithOriginalPrice;
  if (couponAmount !== undefined) {
    const originalPrice = qCopy.price; // Tax-included original
    const discountedPrice = Math.max(0, qCopy.price - couponAmount);
    const discountFactor = qCopy.price > 0 ? discountedPrice / qCopy.price : 0;

    qCopy.price = discountedPrice;
    qCopy.taxAmount = qCopy.taxAmount * discountFactor;
    // The coupon is a flat discount off the tax-inclusive total, so the "before
    // coupon" figure is the original total. Shown next to the after-tax total so
    // the saving equals the coupon value.
    qCopy.priceBeforeCoupon = originalPrice;
  }

  return qCopy;
}

export function useCoupons() {
  const usersApi = useUsersApi();
  const dispatch = useDispatch();
  const couponAmount = useSelector((state: RootState) => state.coupon.amount);

  // Callers fire this without awaiting it. A 401 is left to useSessionExpiry
  // and keeps the stored coupon. With no answer from the API at all, the
  // stored coupon stays and the next refresh tries again. Any other failure
  // is rethrown so it still reaches Sentry.
  async function refreshCoupon() {
    try {
      const couponResponse = await usersApi.getCoupon();
      dispatch(storeCoupon({ amount: couponResponse.amount }));
    } catch (error) {
      if (isUnauthorizedError(error)) {
        return;
      }
      if (isNetworkError(error)) {
        console.warn("Could not refresh the coupon", { error });
        return;
      }
      throw error;
    }
  }

  function discountQuote(q: Quote): QuoteWithOriginalPrice {
    return applyCouponToQuote(q, couponAmount);
  }

  return { couponAmount, discountQuote, refreshCoupon };
}
